import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import ScopeChip from 'components/common/ScopeChip';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { configService, voucherTypeService } from 'api/services';
import type { ResolvedConfigDto, VoucherTypeDto } from 'api/types';

// ============================================================================
// VOUCHER TYPE MODALS — reference-parity with the Account Group / Ledger modals.
// Form modal (create / edit / copy + minimize + concurrency) and View modal.
// ============================================================================

const NUMBERING_METHOD_OPTIONS: SelectOption[] = [
  { value: 'AUTO', label: 'Auto (system-generated)' },
  { value: 'MANUAL', label: 'Manual (user-entered)' },
];
// Matches VOUCHER.RESET_POLICY validation_rule (seed/114). FY = financial-year
// reset (Indian Apr-Mar); DAILY added in F4 alongside YEARLY / MONTHLY / NEVER.
const RESET_OPTIONS: SelectOption[] = [
  { value: 'NEVER', label: 'Never' },
  { value: 'YEARLY', label: 'Yearly (calendar)' },
  { value: 'FY', label: 'Financial Year' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'DAILY', label: 'Daily' },
];

// The four VOUCHER_NUMBERING keys whose effective values we probe against the
// voucher-type-scoped chain — the modal renders a <ScopeChip> next to each
// numbering field showing where its value resolved from (VOUCHER_TYPE
// override / ENTITY inheritance / framework default).
const VOUCHER_NUMBERING_CONFIG_KEYS = [
  'VOUCHER.PREFIX',
  'VOUCHER.SUFFIX',
  'VOUCHER.WIDTH',
  'VOUCHER.RESET_POLICY',
] as const;
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// The classes an opposite-direction note folds into (ADR-0053): a debit-note series lives here.
const DEBIT_NOTE_CLASSES = ['SALES_INVOICE', 'PURCHASE_INVOICE'];
const DOCUMENT_KIND_OPTIONS: SelectOption[] = [
  { value: '', label: 'Invoice / bill (the class\'s own document)' },
  { value: 'DEBIT_NOTE', label: 'Debit Note' },
];

export interface VoucherTypeFormData {
  voucherClass: string;
  /** '' = the class's own document; DEBIT_NOTE (invoice classes only, ADR-0053). Fixed after create. */
  documentKind: string;
  voucherCode: string;
  voucherName: string;
  numberingMethod: string;
  numberPrefix: string;
  numberSuffix: string;
  nextNumber: string;
  numberWidth: string;
  resetFrequency: string;
  allowManualEntry: boolean;
  description: string;
  statusCode: string;
}

const INITIAL_FORM_DATA: VoucherTypeFormData = {
  voucherClass: '',
  documentKind: '',
  voucherCode: '',
  voucherName: '',
  numberingMethod: 'AUTO',
  numberPrefix: '',
  numberSuffix: '',
  nextNumber: '1',
  numberWidth: '0',
  resetFrequency: 'YEARLY',
  allowManualEntry: false,
  description: '',
  statusCode: 'ACTIVE',
};

function buildFormData(vt: VoucherTypeDto | null | undefined, mode: 'create' | 'edit' | 'copy'): VoucherTypeFormData {
  if ((mode === 'edit' || mode === 'copy') && vt) {
    return {
      voucherClass: vt.voucherClass,
      documentKind: vt.documentKind ?? '',
      voucherCode: mode === 'copy' ? '' : vt.voucherCode,
      voucherName: mode === 'copy' ? '' : vt.voucherName,
      numberingMethod: vt.numberingMethod || 'AUTO',
      numberPrefix: vt.numberPrefix ?? '',
      numberSuffix: vt.numberSuffix ?? '',
      nextNumber: String(vt.nextNumber ?? 1),
      numberWidth: String(vt.numberWidth ?? 0),
      resetFrequency: vt.resetFrequency || 'YEARLY',
      allowManualEntry: vt.allowManualEntry,
      description: vt.description ?? '',
      // Empty → backend defaults to ACTIVE via IStatusCache.
      statusCode: mode === 'copy' ? '' : (vt.statusCode || ''),
    };
  }
  return { ...INITIAL_FORM_DATA };
}

function formatSample(d: VoucherTypeFormData): string {
  const n = Number(d.nextNumber) || 1;
  const w = Number(d.numberWidth) || 0;
  const num = w > 0 ? String(n).padStart(w, '0') : String(n);
  return `${d.numberPrefix}${num}${d.numberSuffix}` || '—';
}

// ============================================================================
// FORM MODAL
// ============================================================================

export interface VoucherTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  voucherType?: VoucherTypeDto | null;
  onSave: () => void;
  onClose: () => void;
  openedBy?: string;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, voucherCode?: string) => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

export const VoucherTypeFormModal: React.FC<VoucherTypeFormModalProps> = ({
  isOpen, mode, voucherType, onSave, onClose, openedBy,
  tabId, moduleName, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<VoucherTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(voucherType);
  recordRef.current = voucherType;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // F4: for edit/copy — batch-resolve the 4 numbering keys against the VOUCHER_TYPE-scoped
  // chain so we can render <ScopeChip> next to each numbering field showing whether the
  // value is inherited (ENTITY / ENTERPRISE / SYSTEM) or overridden here (VOUCHER_TYPE).
  // Create mode has no voucher_type_id yet → chips hidden.
  const [numberingScope, setNumberingScope] = useState<Record<string, ResolvedConfigDto | null>>({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const data = buildFormData(recordRef.current, modeRef.current);
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});

    // Fire-and-forget: resolve numbering keys for the voucher-type-scoped chain.
    // Only meaningful when we already know the voucher_type_id (edit or copy of an
    // existing record). Failure is silent — chips just don't render.
    const vtId = recordRef.current?.id;
    if (vtId && (modeRef.current === 'edit' || modeRef.current === 'copy')) {
      Promise.all(
        VOUCHER_NUMBERING_CONFIG_KEYS.map(async (key) => {
          try { return [key, await configService.resolve(key, vtId)] as const; }
          catch { return [key, null] as const; }
        })
      ).then((pairs) => {
        setNumberingScope(Object.fromEntries(pairs));
      });
    } else {
      setNumberingScope({});
    }
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const fd = formDataRef.current;
    const m = modeRef.current;
    const cId = createInstanceIdRef.current;
    const pId = copyInstanceIdRef.current;

    let modalId: string;
    if (m === 'create') modalId = `${tabId}-form-modal-create-${cId || 'default'}`;
    else if (m === 'copy') modalId = `${tabId}-form-modal-copy-${pId || 'default'}`;
    else modalId = `${tabId}-form-modal-edit-${fd.voucherCode || 'new'}`;

    const code = fd.voucherCode?.trim();
    const name = fd.voucherName?.trim();
    let title: string;
    if (m === 'create') { const i = cId || '1'; title = code ? `Add - ${code} (${i})` : name ? `Add - ${name} (${i})` : `Add New (${i})`; }
    else if (m === 'copy') { const i = pId || '1'; title = code ? `Copy - ${code} (${i})` : name ? `Copy - ${name} (${i})` : `Copy (${i})`; }
    else title = `Edit - ${fd.voucherCode || 'Voucher Type'}`;

    const savedFormData = { ...fd };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(fd) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(m, m === 'create' ? cId : m === 'copy' ? pId : undefined, m !== 'create' ? fd.voucherCode : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const getFieldError = (f: string) => fieldErrors[f];
  const clearFieldError = (f: string) => setFieldErrors(prev => { if (!prev[f]) return prev; const n = { ...prev }; delete n[f]; return n; });

  const loadClassOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const classes = await voucherTypeService.getClasses();
    const filtered = input ? classes.filter(c => c.label.toLowerCase().includes(input.toLowerCase())) : classes;
    return filtered.map(c => ({ value: c.value, label: c.label }));
  }, []);

  const classSeed = voucherType ? [{ value: voucherType.voucherClass, label: voucherType.voucherClassName || voucherType.voucherClass }] : undefined;

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!formData.voucherClass) errors.voucherClass = 'Voucher class is required';
    if (!formData.voucherCode.trim()) errors.voucherCode = 'Voucher code is required';
    if (!formData.voucherName.trim()) errors.voucherName = 'Voucher name is required';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }

    if (formData.voucherCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? voucherType?.id : undefined;
        const available = await voucherTypeService.isCodeAvailable(formData.voucherCode.trim(), excludeId);
        if (!available) { setFieldErrors({ voucherCode: 'This code already exists. Please use a different code.' }); return; }
      } catch { /* let server enforce */ }
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, voucherType]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        voucherClass: formData.voucherClass,
        // Create only — the server keeps a type's document kind on update (ADR-0053).
        documentKind: DEBIT_NOTE_CLASSES.includes(formData.voucherClass) && formData.documentKind ? formData.documentKind : null,
        voucherCode: formData.voucherCode.trim(),
        voucherName: formData.voucherName.trim(),
        numberingMethod: formData.numberingMethod,
        numberPrefix: formData.numberPrefix.trim() || null,
        numberSuffix: formData.numberSuffix.trim() || null,
        nextNumber: Number(formData.nextNumber) || 1,
        numberWidth: Number(formData.numberWidth) || 0,
        resetFrequency: formData.resetFrequency,
        allowManualEntry: formData.allowManualEntry,
        description: formData.description.trim() || undefined,
        statusCode: formData.statusCode,
      };
      if (mode === 'edit' && voucherType) {
        await voucherTypeService.update(voucherType.id, { ...payload, rowVersion: voucherType.rowVersion });
      } else {
        await voucherTypeService.create(payload);
      }
      setIsSaving(false);
      const label = mode === 'create' ? 'created' : mode === 'copy' ? 'copied' : 'updated';
      notificationService.toast.success(`Voucher type "${formData.voucherName}" has been ${label} successfully.`);
      onSave();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      if (status === 409 && message) setConcurrencyConflict({ open: true, message });
      else notificationService.toast.error(message || 'An error occurred while saving. Please try again.');
    }
  }, [formData, mode, voucherType, onSave, onClose]);

  const handleRefreshFromConflict = useCallback(async () => {
    if (!voucherType?.id) { setConcurrencyConflict({ open: false, message: '' }); return; }
    setIsRefreshing(true);
    try {
      const res = await voucherTypeService.getById(voucherType.id);
      const fresh = buildFormData(res.data, 'edit');
      setFormData(fresh);
      initialSnapshotRef.current = JSON.stringify(fresh);
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.info('Form refreshed with latest data. Please review and save again.');
    } catch {
      notificationService.toast.error('Could not refresh from server. Please close and re-open the record.');
    } finally { setIsRefreshing(false); }
  }, [voucherType]);

  if (process.env.NODE_ENV === 'development') {
    console.log('VoucherTypeFormModal opened', { openedBy: openedBy || 'Unknown', mode });
  }

  if (!isOpen) return null;

  const auto = formData.numberingMethod === 'AUTO';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'create' ? 'Creating voucher type...' : mode === 'copy' ? 'Copying voucher type...' : 'Saving changes...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' && 'Add Voucher Type'}
            {mode === 'edit' && 'Edit Voucher Type'}
            {mode === 'copy' && 'Copy Voucher Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="voucherClass">
              <SyutSelect label={<>Voucher Class <span className="text-danger">*</span></>} isAsync loadOptions={loadClassOptions}
                defaultOptions cacheOptions={false} seedOptions={classSeed} menuPortalTarget={document.body} value={formData.voucherClass}
                onChange={(val) => {
                  clearFieldError('voucherClass');
                  const cls = (val as string) || '';
                  setFormData(prev => ({ ...prev, voucherClass: cls, documentKind: DEBIT_NOTE_CLASSES.includes(cls) ? prev.documentKind : '' }));
                }}
                placeholder="Select class..." isClearable error={getFieldError('voucherClass')} />
            </div>

            {DEBIT_NOTE_CLASSES.includes(formData.voucherClass) && (
              <div className="form-group" data-field="documentKind">
                <SyutSelect label="Numbers" options={DOCUMENT_KIND_OPTIONS} menuPortalTarget={document.body} value={formData.documentKind}
                  onChange={(val) => setFormData(prev => ({ ...prev, documentKind: (val as string) || '' }))}
                  isClearable={false} isDisabled={mode === 'edit'} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                  {mode === 'edit'
                    ? 'Fixed once created — a series in use never changes what its documents are.'
                    : 'Debit Note: raises what is owed after the invoice, with its own number series.'}
                </div>
              </div>
            )}

            <div className="form-group" data-field="statusCode">
              <SyutSelect label="Status" options={STATUS_OPTIONS} menuPortalTarget={document.body} value={formData.statusCode}
                onChange={(val) => setFormData(prev => ({ ...prev, statusCode: (val as string) || '' }))} isClearable={false} />
            </div>

            <div className="form-group">
              <label className="form-label">Voucher Code <span className="text-danger">*</span></label>
              <input type="text" data-field="voucherCode" className={`form-input${getFieldError('voucherCode') ? ' form-field-error' : ''}`}
                value={formData.voucherCode} maxLength={30} placeholder="e.g., BANK_PMT"
                onChange={(e) => { clearFieldError('voucherCode'); setFormData(prev => ({ ...prev, voucherCode: e.target.value.toUpperCase() })); }}
                readOnly={mode === 'edit'} style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}} />
              {getFieldError('voucherCode') && <div className="form-error-message">{getFieldError('voucherCode')}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Voucher Name <span className="text-danger">*</span></label>
              <input type="text" data-field="voucherName" className={`form-input${getFieldError('voucherName') ? ' form-field-error' : ''}`}
                value={formData.voucherName} maxLength={200} placeholder="e.g., Bank Payment"
                onChange={(e) => { clearFieldError('voucherName'); setFormData(prev => ({ ...prev, voucherName: e.target.value })); }} />
              {getFieldError('voucherName') && <div className="form-error-message">{getFieldError('voucherName')}</div>}
            </div>

            <div className="form-group" data-field="numberingMethod">
              <SyutSelect label="Numbering" options={NUMBERING_METHOD_OPTIONS} menuPortalTarget={document.body} value={formData.numberingMethod}
                onChange={(val) => setFormData(prev => ({ ...prev, numberingMethod: (val as string) || 'AUTO' }))} isClearable={false} />
            </div>

            <div className="form-group" data-field="resetFrequency">
              <SyutSelect
                label={
                  <>
                    Number Reset{' '}
                    <ScopeChip
                      resolvedFromScopeType={numberingScope['VOUCHER.RESET_POLICY']?.resolvedScopeType}
                      resolvedFromScopeName={numberingScope['VOUCHER.RESET_POLICY']?.resolvedScopeDisplayName}
                      currentScopeType="VOUCHER_TYPE"
                      fromDefault={numberingScope['VOUCHER.RESET_POLICY']?.fromDefault}
                      compact
                    />
                  </>
                }
                options={RESET_OPTIONS} menuPortalTarget={document.body} value={formData.resetFrequency}
                onChange={(val) => setFormData(prev => ({ ...prev, resetFrequency: (val as string) || 'YEARLY' }))} isClearable={false} />
            </div>

            <div className="form-group">
              <label className="form-label">
                Number Prefix{' '}
                <ScopeChip
                  resolvedFromScopeType={numberingScope['VOUCHER.PREFIX']?.resolvedScopeType}
                  resolvedFromScopeName={numberingScope['VOUCHER.PREFIX']?.resolvedScopeDisplayName}
                  currentScopeType="VOUCHER_TYPE"
                  fromDefault={numberingScope['VOUCHER.PREFIX']?.fromDefault}
                  compact
                />
              </label>
              <input type="text" className="form-input" value={formData.numberPrefix} maxLength={20} placeholder="e.g., SI-"
                onChange={(e) => setFormData(prev => ({ ...prev, numberPrefix: e.target.value }))} disabled={!auto} />
            </div>

            <div className="form-group">
              <label className="form-label">
                Number Suffix{' '}
                <ScopeChip
                  resolvedFromScopeType={numberingScope['VOUCHER.SUFFIX']?.resolvedScopeType}
                  resolvedFromScopeName={numberingScope['VOUCHER.SUFFIX']?.resolvedScopeDisplayName}
                  currentScopeType="VOUCHER_TYPE"
                  fromDefault={numberingScope['VOUCHER.SUFFIX']?.fromDefault}
                  compact
                />
              </label>
              <input type="text" className="form-input" value={formData.numberSuffix} maxLength={20} placeholder="e.g., /25-26"
                onChange={(e) => setFormData(prev => ({ ...prev, numberSuffix: e.target.value }))} disabled={!auto} />
            </div>

            <div className="form-group">
              <label className="form-label">Next Number</label>
              <input type="number" className="form-input" value={formData.nextNumber} min={1}
                onChange={(e) => setFormData(prev => ({ ...prev, nextNumber: e.target.value }))} disabled={!auto} />
            </div>

            <div className="form-group">
              <label className="form-label">
                Number Width (zero-pad){' '}
                <ScopeChip
                  resolvedFromScopeType={numberingScope['VOUCHER.WIDTH']?.resolvedScopeType}
                  resolvedFromScopeName={numberingScope['VOUCHER.WIDTH']?.resolvedScopeDisplayName}
                  currentScopeType="VOUCHER_TYPE"
                  fromDefault={numberingScope['VOUCHER.WIDTH']?.fromDefault}
                  compact
                />
              </label>
              <input type="number" className="form-input" value={formData.numberWidth} min={0}
                onChange={(e) => setFormData(prev => ({ ...prev, numberWidth: e.target.value }))} disabled={!auto} />
            </div>
          </div>

          {auto && (
            <div style={{ margin: '0.25rem 0 0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Sample document number: <strong>{formatSample(formData)}</strong>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', margin: '0.25rem 0 0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.allowManualEntry} onChange={(e) => setFormData(prev => ({ ...prev, allowManualEntry: e.target.checked }))} />
              <span className="form-label" style={{ margin: 0 }}>Allow manual number override</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={formData.description} rows={2}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            <IconSystem.check size={16} />
            {mode === 'edit' ? 'Save Changes' : mode === 'copy' ? 'Create Copy' : 'Create Voucher Type'}
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to "${formData.voucherName}"?` : mode === 'copy' ? `Create voucher type as "${formData.voucherName}"?` : `Create voucher type "${formData.voucherName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />

      <ConfirmDialog open={concurrencyConflict.open} title="Record Modified by Another User"
        message={concurrencyConflict.message || 'This record was modified by another user while your form was open. Refresh to load their changes, or keep your current values.'}
        confirmLabel={isRefreshing ? 'Refreshing...' : 'Refresh from Server'} cancelLabel="Keep My Changes" variant="default"
        loading={isRefreshing} onConfirm={handleRefreshFromConflict} onCancel={() => setConcurrencyConflict({ open: false, message: '' })} />
    </>
  );
};

// ============================================================================
// VIEW MODAL
// ============================================================================

export interface VoucherTypeViewModalProps {
  isOpen: boolean;
  voucherType: VoucherTypeDto | null;
  onClose: () => void;
  onEdit?: (vt: VoucherTypeDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (voucherCode: string) => void;
}

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px' }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export const VoucherTypeViewModal: React.FC<VoucherTypeViewModalProps> = ({
  isOpen, voucherType, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !voucherType) return;
    minimize({
      id: `${tabId}-view-modal-${voucherType.voucherCode}`,
      title: `View - ${voucherType.voucherCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(voucherType.voucherCode),
    });
    onClose();
  }, [tabId, moduleName, voucherType, minimize, onClose, onRestoreView]);

  if (!isOpen || !voucherType) return null;

  const vt = voucherType;
  const sample = (() => {
    const n = vt.nextNumber || 1;
    const num = vt.numberWidth > 0 ? String(n).padStart(vt.numberWidth, '0') : String(n);
    return `${vt.numberPrefix ?? ''}${num}${vt.numberSuffix ?? ''}`;
  })();

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Voucher Type Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={vt.voucherCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px', display: 'flex', gap: '0.5rem' }}>
                <span className="badge" style={vt.statusColor ? { background: vt.statusColor, color: '#fff' } : undefined}>{vt.statusName || 'Active'}</span>
                <span className="badge">{vt.isSystem ? 'System' : 'Custom'}</span>
              </div>
            </div>
            <ViewField label="Name" value={vt.voucherName} fullWidth />
            <ViewField label="Class" value={`${vt.voucherClassName || vt.voucherClass}${vt.documentKind === 'DEBIT_NOTE' ? ' — Debit Note' : ''}`} />
            <ViewField label="Numbering" value={vt.numberingMethod} />
            <ViewField label="Prefix" value={vt.numberPrefix} />
            <ViewField label="Suffix" value={vt.numberSuffix} />
            <ViewField label="Next Number" value={vt.nextNumber} />
            <ViewField label="Number Width" value={vt.numberWidth} />
            <ViewField label="Reset" value={vt.resetFrequency} />
            <ViewField label="Sample Number" value={sample} />
            <ViewField label="Allow Manual Override" value={vt.allowManualEntry ? 'Yes' : 'No'} />
            <ViewField label="Description" value={vt.description} fullWidth />
            <ViewField label="Created By" value={vt.createdByName || '—'} />
            <ViewField label="Created At" value={vt.createdAt ? new Date(vt.createdAt).toLocaleString() : null} />
            {vt.updatedAt && (
              <>
                <ViewField label="Updated By" value={vt.updatedByName || '—'} />
                <ViewField label="Updated At" value={new Date(vt.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(vt); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default VoucherTypeFormModal;
